/** @jsx jsx */
import React, { memo, useMemo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Placement } from '@post-office/placements/jira-nin-saved-filters-menu';
import Button from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { cssMap, jsx } from '@atlaskit/css';
import type { TriggerProps } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import { SpotlightManager } from '@atlaskit/onboarding';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { fg } from '@atlassian/jira-feature-gating';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import type { FiltersMenu_project$key } from '@atlassian/jira-relay/src/__generated__/FiltersMenu_project.graphql';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import messages from './FiltersMenu.messages.tsx';
import { filtersMenuEntryPoint } from './entrypoint.tsx';

const styles = cssMap({
	fallback: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '300px',
		height: '200px',
	},
});

type FiltersMenuProps = {
	defaultJql: string;
	onChangeJql: (jql: string, clearFilter: boolean) => void;
	projectFragment: FiltersMenu_project$key;
};

const experience = 'viewSavedFilters';

const FiltersMenuComponent = ({ defaultJql, onChangeJql, projectFragment }: FiltersMenuProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectData = useFragment(
		graphql`
			fragment FiltersMenu_project on JiraProject {
				id @required(action: THROW)
			}
		`,
		projectFragment,
	);

	const { formatMessage } = useIntl();

	const entryPointParams = useMemo(
		() => ({
			projectId: projectData.id,
			first: 50,
			includeProjectClause: true,
		}),
		[projectData.id],
	);

	const entryPointProps = useMemo(
		() => ({
			defaultJql,
			onChangeJql: (jql: string) => onChangeJql(jql, true),
		}),
		[defaultJql, onChangeJql],
	);

	const startExperience = useExperienceStart({
		experience,
		analyticsSource: 'issue-navigator',
	});

	const trigger = useCallback(
		(triggerProps: TriggerProps) => (
			<Button
				{...triggerProps}
				appearance="subtle"
				iconAfter={() => <ChevronDownIcon label="" size="small" />}
				isSelected={triggerProps['aria-expanded']}
				onClick={() => {
					fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'savedFiltersMenu');
					startExperience();
				}}
				testId="issue-navigator-filters-menu.ui.trigger"
			>
				{formatMessage(messages.menuTitle)}
			</Button>
		),
		[createAnalyticsEvent, formatMessage, startExperience],
	);

	const fallback = useMemo(
		() => (
			<div css={styles.fallback}>
				<Spinner />
			</div>
		),
		[],
	);

	const popupTrigger = (
		<ModalContextProvider>
			<PopupTrigger
				entryPoint={filtersMenuEntryPoint}
				entryPointParams={entryPointParams}
				entryPointProps={entryPointProps}
				fallback={fallback}
				placement="bottom-end"
				role="dialog"
				shouldRenderToParent
				trigger={trigger}
				label={formatMessage(messages.filtersMenuLabel)}
			/>
		</ModalContextProvider>
	);

	if (!__SERVER__ && fg('jira-nin-saved-filters-menu-onboarding')) {
		return (
			<SpotlightManager blanketIsTinted={false}>
				<Placement>{popupTrigger}</Placement>
			</SpotlightManager>
		);
	}

	return popupTrigger;
};

export const FiltersMenu = memo(FiltersMenuComponent);
